import { useState, useRef, useMemo } from 'react'
import { storage } from '../../utils/storage'

/**
 * useSharedMapState - Consolidated state management for both map components
 * 
 * Manages all core map state that's duplicated between HomePageMap and EntityDetailMap:
 * - Entities list
 * - User location (GPS or geocoded)
 * - Search parameters (location, radius, name)
 * - UI flags (showUserLocation, isLoading)
 * - Refs (map, markers, popup tracking)
 * - Computed values (activeMapLocation)
 * 
 * @param {Object} options - Configuration options
 * @param {boolean} options.isEntityMode - Whether in entity-specific mode
 * @param {string} options.entityId - Entity ID for entity mode
 * @returns {Object} Shared state and setters
 * 
 * @idempotent - Manages isolated state per map instance
 * @atomic - Single source of truth for core map state
 */
export function useSharedMapState({ isEntityMode = false, entityId = null } = {}) {
  // Core entity and location state
  const [entities, setEntities] = useState([])
  const [userLocation, setUserLocation] = useState(null)
  const [radius, setRadius] = useState(isEntityMode ? 25 : 50) // Entity mode uses smaller default radius
  const [searchLocation, setSearchLocation] = useState('')
  const [searchLocationName, setSearchLocationName] = useState('')
  
  // UI state
  const [showUserLocation, setShowUserLocation] = useState(() => {
    // Initialize from storage, default to false (less intrusive)
    return storage.get('showUserLocation', false)
  })
  const [isLoading, setIsLoading] = useState(false)
  
  // Refs for map functionality
  const mapRef = useRef(null) // Reference to the map instance
  const markerRefs = useRef({}) // Store refs to all markers for programmatic popup control
  const currentOpenPopupRef = useRef(null) // Track the currently open popup to close it when another opens
  const hasInitialized = useRef(false) // Track if initial data fetch has happened
  const hasShownLocationError = useRef(false) // Track if we've shown location error toast
  const lastFetchTime = useRef(0) // Track last fetch timestamp for navigation detection
  const previousLocationKey = useRef(null) // Track previous location key to detect navigation
  const savedMapViewRef = useRef(null) // Save map state before spot mode
  
  // Computed active location for "nearby" filtering
  // Priority: userLocation (GPS or search result) > first entity's location
  const activeMapLocation = useMemo(() => {
    // Use userLocation if available (could be from GPS or geocoded search)
    if (userLocation) {
      return {
        lat: userLocation.latitude,
        lng: userLocation.longitude
      }
    }
    // Fall back to first entity's location if we have entities but no userLocation
    if (entities.length > 0 && entities[0].currentLatitude && entities[0].currentLongitude) {
      return {
        lat: entities[0].currentLatitude,
        lng: entities[0].currentLongitude
      }
    }
    return null
  }, [userLocation, entities])
  
  return {
    // State
    entities,
    setEntities,
    userLocation,
    setUserLocation,
    radius,
    setRadius,
    searchLocation,
    setSearchLocation,
    searchLocationName,
    setSearchLocationName,
    showUserLocation,
    setShowUserLocation,
    isLoading,
    setIsLoading,
    
    // Refs
    mapRef,
    markerRefs,
    currentOpenPopupRef,
    hasInitialized,
    hasShownLocationError,
    lastFetchTime,
    previousLocationKey,
    savedMapViewRef,
    
    // Computed
    activeMapLocation
  }
}
